import { useDispatch, useSelector } from "react-redux";
import { getSalesAccountChannel } from "services/pusher/pusher-utils";
import { RootState } from "store";
import { PusherEvents } from "services/pusher/pusher-events.enum";
import Pusher from "pusher-js";
import { useEffect, useState } from "react";
import { Environments, getEnvironment } from "utils/getEnvironment";
import { apiPaths } from "enum/paths.enum";
import { fetchCurrentLabelJobsThunk } from "store/currentLabelJobsSlice/index.slice";
import { fetchShipmentsThunk } from "store/shipmentsSlice/thunks/fetchShipmentsThunk.thunk";
import { fetchQuotesThunk } from "store/quotesSlice/QuotesSliceFetchQuotesThunk.Thunk";
import { toast } from "react-hot-toast";

if (getEnvironment() !== Environments.production) {
  Pusher.logToConsole = false;
}

/** Window inside which label-job completions share one shipments refetch. */
const LABEL_JOB_REFETCH_WINDOW_MS = 2000;

export const PusherData = ({ children }: { children: React.ReactNode }) => {
  const { salesAccountUUID, isAuthenticated } = useSelector(
    (state: RootState) => {
      return state.authSlice;
    }
  );

  const dispatch = useDispatch();

  const [pusher, setPusher] = useState<Pusher | null>(null);

  useEffect(() => {
    if (salesAccountUUID && isAuthenticated) {
      const pusher = new Pusher(process.env.NEXT_PUBLIC_PUSHER_KEY || "", {
        cluster: process.env.NEXT_PUBLIC_PUSHER_CLUSTER,
        forceTLS: false,
        authEndpoint:
          process.env.NEXT_PUBLIC_NEST_API_BASE_URL + apiPaths.pusher,
        auth: {
          headers: {
            authorization: "Bearer " + window["accessToken"],
          },
        },
      });

      setPusher(pusher);
    }
  }, [salesAccountUUID, isAuthenticated]);

  useEffect(() => {
    if (pusher) {
      const channel = pusher.subscribe(
        getSalesAccountChannel(salesAccountUUID)
      );

      // Every label job for the account fires this in every open session, so
      // a warehouse printing one label at a time produced bursts of identical
      // shipments-list refetches (13 days of logs: 38% of all shipments
      // searches were repeats within 10 s of a label job). The list refetch
      // is coalesced: the first completion refreshes at once, further
      // completions inside the window collapse into one refresh after the
      // burst. The label jobs list itself stays immediate.
      let refetchTimer: ReturnType<typeof setTimeout> | null = null;
      let refetchPending = false;
      const refetchShipmentsCoalesced = () => {
        if (refetchTimer) {
          refetchPending = true;
          return;
        }
        dispatch(fetchShipmentsThunk());
        refetchTimer = setTimeout(() => {
          refetchTimer = null;
          if (refetchPending) {
            refetchPending = false;
            refetchShipmentsCoalesced();
          }
        }, LABEL_JOB_REFETCH_WINDOW_MS);
      };

      channel.bind(PusherEvents.LABEL_JOB_COMPLETED, () => {
        dispatch(fetchCurrentLabelJobsThunk());
        refetchShipmentsCoalesced();
      });

      channel.bind(PusherEvents.QUOTE_CREATED, () => {
        dispatch(fetchQuotesThunk());
      });
    }
  }, [pusher, dispatch]);

  return <>{children}</>;
};
